import React, { useState, useMemo, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Fade,
  Backdrop,
  alpha,
  Paper,
  IconButton,
  Autocomplete,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  SERVICE_TYPES,
  FREQUENCY_TYPES,
  STATUS_LABELS,
  DEFAULT_FORM_VALUES,
} from "../../constants";
import { validateClosingHour } from "../../../../utils/formatters";
import { sortClients, sortBranches, sortTechnicians } from "../../../../utils/sortUtils";
import TimeInput24H from "../../../../components/TimeInput24H";

/**
 * Modal mejorado para crear un nuevo servicio
 */
const CreateServiceModal = ({
  open,
  onClose,
  onSubmit,
  clientes,
  sucursales,
  tecnicos,
  loadingSelects,
  onClientChange,
}) => {
  const [form, setForm] = useState(DEFAULT_FORM_VALUES);
  const [validationError, setValidationError] = useState(null);


  const LOCKED_TIME_TYPES = [
    { value: "MIP", label: "Servicio MIP" },
    { value: "NEBULIZACION", label: "Nebulizacion" },
    { value: "MICROINYECCION", label: "Microinyección" },
    { value: "MOSCA", label: "Mosca" },
    { value: "ABEJA", label: "Abeja" },
  ];
  // Resetear el formulario cada vez que se abre el modal
  useEffect(() => {
    if (open) {
      setForm(DEFAULT_FORM_VALUES);
      setValidationError(null);
    }
  }, [open]);

  const branchContainsContract = (branchName = "", contract) => {
    if (!contract) return false;
  
    // Busca el número como palabra completa (evita falsos positivos)
    const regex = new RegExp(`\\b${contract}\\b`);
    return regex.test(branchName);
  };  
  
  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value || "" }));
    // Limpiar error de validación al cambiar campos
    if (validationError) {
      setValidationError(null);
    }
  };

  const handleClientChange = async (clientId) => {
    handleChange("client", clientId);
    handleChange("branch", "");
    if (onClientChange) {
      await onClientChange(clientId);
    }
  };

  const handleContractNumberChange = (value) => {
    handleChange("contractNumber", value);
  
    if (!value || !sucursales.length) return;
  
    const matchedBranch = sucursales.find((s) =>
      branchContainsContract(s.name, value)
    );
  
    if (matchedBranch) {
      handleChange("branch", matchedBranch._id);
    } else {
      handleChange("branch", "");
    }
  };  

  // Obtener la hora de cierre de la sucursal seleccionada
  const selectedBranch = useMemo(() => {
    return sucursales.find((s) => s._id === form.branch) || null;
  }, [sucursales, form.branch]);

  const closingHour = selectedBranch?.closingHour || null;

  // Ordenar datos alfabéticamente
  const sortedClientes = useMemo(() => sortClients(clientes), [clientes]);
  const sortedSucursales = useMemo(() => sortBranches(sucursales), [sucursales]);
  const sortedTecnicos = useMemo(() => sortTechnicians(tecnicos), [tecnicos]);

  // Validar hora de cierre
  const timeValidation = useMemo(() => {
    // Solo validar si hay fecha, hora y sucursal seleccionada
    if (!form.date || !form.hour || !form.type) {
      return { isValid: true, errorMessage: null };
    }
    
    // Solo validar hora de cierre si es servicio protegido
    const isProtectedTimeType = LOCKED_TIME_TYPES.find((t) => t.value === form.type);
    if (isProtectedTimeType) {
      return validateClosingHour(form.date, form.hour, closingHour);
    }
    return { isValid: true, errorMessage: null };
  }, [form.date, form.hour, form.type, closingHour]);

  const handleSubmit = () => {
    // Validar campos requeridos
    if (!form.type || !form.client || !form.branch || !form.date) {
      setValidationError("Completa todos los campos requeridos");
      return;
    }

    // Validar hora de cierre
    if (!timeValidation.isValid) {
      setValidationError(timeValidation.errorMessage || "La hora del servicio no es válida");
      return;
    }

    setValidationError(null);
    onSubmit(form);
  };

  const handleClose = () => {
    setForm(DEFAULT_FORM_VALUES);
    setValidationError(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      BackdropProps={{
        sx: {
          background: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(2px)",
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: 520 },
            maxHeight: "85vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            }}
          >
            {/* Header limpio */}
            <Box
              sx={{
                background: "#fff",
                p: 2,
                borderBottom: `1px solid ${alpha("#000", 0.08)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    p: 0.75,
                    borderRadius: 1,
                    background: alpha("#1976d2", 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AddCircleOutlineIcon sx={{ fontSize: 20, color: "#1976d2" }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "text.primary" }}>
                  Nuevo Servicio
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    background: alpha("#000", 0.05),
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Form Content con scroll */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                p: 2.5,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  background: alpha("#1976d2", 0.05),
                },
                "&::-webkit-scrollbar-thumb": {
                  background: alpha("#1976d2", 0.2),
                  borderRadius: 1,
                  "&:hover": {
                    background: alpha("#1976d2", 0.3),
                  },
                },
              }}
            >
              <Stack spacing={2}>
                <TextField
                  select
                  size="small"
                  label="Tipo de servicio"
                  value={form.type}
                  onChange={(e) => handleChange("type", e.target.value)}
                  fullWidth
                  required
                >
                  <MenuItem value="">-- Seleccionar --</MenuItem>
                  {SERVICE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Cliente"
                  value={form.client}
                  onChange={(e) => handleClientChange(e.target.value)}
                  fullWidth
                  required
                >
                  <MenuItem value="">-- Seleccionar --</MenuItem>
                  {sortedClientes.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>

                <Box display="flex" gap={1.5}>
                  {/* BUSQUEDA POR NUMERO DE CONTRATO */}
                  <TextField
                    size="small"
                    label="Número de contrato"
                    value={form.contractNumber}
                    onChange={(e) => handleContractNumberChange(e.target.value)}
                    disabled={!form.client || loadingSelects}
                    fullWidth
                  />

                  {/* BUSQUEDA POR SUCURSAL */}
                  <TextField
                    select
                    size="small"
                    label="Sucursal"
                    value={form.branch}
                    onChange={(e) => handleChange("branch", e.target.value)}
                    disabled={!form.client || loadingSelects}
                    fullWidth
                    required
                  >
                    <MenuItem value="">-- Seleccionar --</MenuItem>
                    {sortedSucursales.map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <Box display="flex" gap={1.5}>
                  <TextField
                    type="date"
                    size="small"
                    label="Fecha"
                    value={form.date}
                    onChange={(e) => handleChange("date", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    required
                  />
                  <TimeInput24H
                    size="small"
                    label="Hora (24H)"
                    value={form.hour}
                    onChange={(e) => handleChange("hour", e.target.value)}
                    fullWidth
                    error={!timeValidation.isValid}
                    helperText={!timeValidation.isValid ? timeValidation.errorMessage : null}
                  />
                </Box>

                <TextField
                  select
                  size="small"
                  label="Recurrencia"
                  value={form.frequency}
                  onChange={(e) => handleChange("frequency", e.target.value)}
                  fullWidth
                >
                  <MenuItem value="">-- Seleccionar --</MenuItem>
                  {FREQUENCY_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Autocomplete
                  multiple
                  options={sortedTecnicos}
                  getOptionLabel={(option) => option.name}
                  value={
                    sortedTecnicos.filter((t) =>
                      (form.technicians || []).includes(t._id)
                    )
                  }
                  onChange={(_, value) =>
                    handleChange(
                      "technicians",
                      value.map((t) => t._id)
                    )
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        size="small"
                        {...getTagProps({ index })}
                        key={option._id}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Técnicos asignados"
                      placeholder="Seleccionar técnicos"
                      size="small"
                    />
                  )}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2">
                    Técnicos asignados
                  </Typography>

                  <Chip
                    size="small"
                    color="primary"
                    label={`${form.technicians?.length || 0} seleccionados`}
                  />
                </Box>
                
                <TextField
                  select
                  size="small"
                  label="Estado"
                  value={form.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                  fullWidth
                >
                  {Object.entries(STATUS_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  size="small"
                  label="Notas"
                  value={form.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                />
              </Stack>
            </Box>

            {/* Footer limpio */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${alpha("#000", 0.08)}`,
                background: "#fff",
                flexShrink: 0,
                display: "flex",
                gap: 1.5,
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={handleClose} size="small" sx={{ fontWeight: 600, minWidth: 80 }}>
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                size="small"
                disabled={!timeValidation.isValid}
                sx={{
                  background: "#1976d2",
                  fontWeight: 700,
                  minWidth: 100,
                  "&:hover": {
                    background: "#1565c0",
                  },
                }}
              >
                Guardar
              </Button>
            </Box>
          </Paper>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CreateServiceModal;
